interface SaveForTrainingRequest {
  message_id: string;
  conversation_id?: string;
  client_id?: string;
  category?: string;
  metadata?: any;
}

interface SaveForTrainingResponse {
  status: string;
  message: string;
  candidate_id?: string;
}

interface QACandidate {
  id: string;
  client_id: string;
  conversation_id: string;
  question: string;
  answer: string;
  context: any;
  category: string | null;
  source_url: string | null;
  thumbs_up_count: number;
  thumbs_down_count: number;
  times_saved: number;
  status: 'pending' | 'approved' | 'rejected' | 'needs_revision';
  reviewed_by: string | null;
  reviewed_at: string | null;
  reviewer_notes: string | null;
  times_used: number;
  avg_satisfaction: number | null;
  last_used: string | null;
  created_at: string;
  training_status?: 'pending' | 'approved' | 'in_job' | 'fine_tuned' | 'removed' | null;
  training_type?: 'single' | 'conversation' | null;
  training_group_id?: string | null;
  job_id?: string | null;
  removed_at?: string | null;
  removal_reason?: string | null;
}

interface SimilarQA {
  id: string;
  question: string;
  answer: string;
  similarity: number;
  category: string | null;
  created_at: string | null;
}

class LearnedQAService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
  }

  async saveForTraining(request: SaveForTrainingRequest): Promise<SaveForTrainingResponse> {
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      const authToken = localStorage.getItem('auth_token');
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }

      const response = await fetch(`${this.baseUrl}/api/feedback/save-for-training`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ...request,
          metadata: {
            guest_token: localStorage.getItem('guest_token') || undefined,
            ...request.metadata
          }
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error saving for training:', error);
      throw error;
    }
  }

  async getCandidates(clientId: string, limit: number = 50, offset: number = 0): Promise<QACandidate[]> {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(
        `${this.baseUrl}/api/learned-qa/${clientId}/candidates?limit=${limit}&offset=${offset}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.candidates || [];
    } catch (error) {
      console.error('Error getting candidates:', error);
      throw error;
    }
  }

  async getCandidate(clientId: string, candidateId: string): Promise<QACandidate> {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(
        `${this.baseUrl}/api/learned-qa/${clientId}/candidates/${candidateId}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.candidate;
    } catch (error) {
      console.error('Error getting candidate:', error);
      throw error;
    }
  }

  async getCandidateByMessageId(
    clientId: string,
    messageId: string,
    conversationId: string
  ): Promise<QACandidate | null> {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(
        `${this.baseUrl}/api/learned-qa/${clientId}/candidates/by-message/${messageId}?conversation_id=${conversationId}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.candidate || null;
    } catch (error) {
      console.error('Error getting candidate by message ID:', error);
      return null;
    }
  }

  async approveCandidate(
    clientId: string,
    candidateId: string,
    reviewerId: string,
    notes?: string
  ): Promise<void> {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(
        `${this.baseUrl}/api/learned-qa/${clientId}/candidates/${candidateId}/approve`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            reviewer_id: reviewerId,
            notes: notes
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error('Error approving candidate:', error);
      throw error;
    }
  }

  async rejectCandidate(
    clientId: string,
    candidateId: string,
    reviewerId: string,
    reason?: string
  ): Promise<void> {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(
        `${this.baseUrl}/api/learned-qa/${clientId}/candidates/${candidateId}/reject`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            reviewer_id: reviewerId,
            reason: reason
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error('Error rejecting candidate:', error);
      throw error;
    }
  }

  async editAndApproveCandidate(
    clientId: string,
    candidateId: string,
    reviewerId: string,
    editedQuestion?: string,
    editedAnswer?: string,
    notes?: string
  ): Promise<void> {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(
        `${this.baseUrl}/api/learned-qa/${clientId}/candidates/${candidateId}/edit`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            reviewer_id: reviewerId,
            edited_question: editedQuestion,
            edited_answer: editedAnswer,
            notes: notes
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error('Error editing and approving candidate:', error);
      throw error;
    }
  }

  async findSimilarQA(
    clientId: string,
    question: string,
    answer: string
  ): Promise<SimilarQA[]> {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(
        `${this.baseUrl}/api/learned-qa/${clientId}/similar`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            question: question,
            answer: answer
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.similar_qa || [];
    } catch (error) {
      console.error('Error finding similar Q&A:', error);
      throw error;
    }
  }

  async extractQAFromConversation(
    clientId: string,
    conversationId: string,
    messageId?: string
  ): Promise<string> {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(
        `${this.baseUrl}/api/feedback/save-for-training`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            client_id: clientId,
            conversation_id: conversationId,
            ...(messageId ? { message_id: String(messageId) } : {}),
            category: null,
            metadata: {}
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.candidate_id || data.id || '';
    } catch (error) {
      console.error('Error extracting Q&A from conversation:', error);
      throw error;
    }
  }

  async removeFromTraining(
    clientId: string,
    candidateId: string,
    removeWholeConversation: boolean = false
  ): Promise<void> {
    console.log('[learnedQAService.removeFromTraining] Called', { clientId, candidateId, removeWholeConversation });
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const url = `${this.baseUrl}/api/learned-qa/${clientId}/candidates/${candidateId}/remove-from-training?remove_whole_conversation=${removeWholeConversation}`;
      console.log('[learnedQAService.removeFromTraining] Making request to:', url);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      console.log('[learnedQAService.removeFromTraining] Response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error('Error removing from training:', error);
      throw error;
    }
  }

  // ============= ENHANCED TRAINING MANAGEMENT METHODS =============

  async approveSingleMessage(
    clientId: string,
    candidateId: string
  ): Promise<{
    success: boolean;
    training_type: string;
    warning?: string;
    threshold_status?: any;
  }> {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(
        `${this.baseUrl}/api/learned-qa/${clientId}/candidates/${candidateId}/approve-single`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error approving single message:', error);
      throw error;
    }
  }

  async approveWholeConversation(
    clientId: string,
    conversationId: string
  ): Promise<{
    success: boolean;
    training_type: string;
    messages_approved: number;
    superseded_singles: number;
    training_group_id: string;
    threshold_status?: any;
  }> {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(
        `${this.baseUrl}/api/learned-qa/${clientId}/conversations/${conversationId}/approve`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error approving whole conversation:', error);
      throw error;
    }
  }

  async removeFromTrainingEnhanced(
    clientId: string,
    candidateId?: string,
    groupId?: string,
    reason: string = 'User requested removal'
  ): Promise<{
    success: boolean;
    action_taken?: string;
    previous_status?: string;
    messages_removed?: number;
  }> {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      let url: string;
      if (candidateId) {
        url = `${this.baseUrl}/api/learned-qa/${clientId}/candidates/${candidateId}/training`;
      } else if (groupId) {
        url = `${this.baseUrl}/api/learned-qa/${clientId}/groups/${groupId}/training`;
      } else {
        throw new Error('Either candidateId or groupId must be provided');
      }

      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ reason })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error removing from training:', error);
      throw error;
    }
  }

  async getTrainingSummary(clientId: string): Promise<{
    counts: {
      pending_review: number;
      approved: number;
      in_job: number;
      fine_tuned: number;
      removed: number;
    };
    active_jobs: Array<{
      job_id: string;
      status: string;
      created_at: string;
    }>;
    threshold_status: {
      approved: number;
      threshold: number;
      needed: number;
      ready: boolean;
      message: string;
    };
    total_candidates: number;
  }> {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(
        `${this.baseUrl}/api/learned-qa/${clientId}/training-summary`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting training summary:', error);
      throw error;
    }
  }
}

export const learnedQAService = new LearnedQAService();
export default learnedQAService;

